(() => {
  class QuickViewModal extends HTMLElement {
    constructor() {
      super();

      this.isOpen = false;
      this.activeTrigger = null;
      this.currentProductUrl = '';
      this.productCache = new Map();
      this.pendingRequests = new Map();
      this.cacheVersion = 0;
      this.refreshController = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onModalSubmit = this.onModalSubmit.bind(this);
      this.onCartChanged = this.onCartChanged.bind(this);
      this.onPrefetch = this.onPrefetch.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.content = this.querySelector('[data-quick-view-content]');
      this.error = this.querySelector('[data-quick-view-error]');
      this.dialog = this.querySelector('[data-quick-view-dialog]');

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('pointerover', this.onPrefetch);
      document.addEventListener('focusin', this.onPrefetch);
      document.addEventListener('product:added', this.onCartChanged);
      document.addEventListener('cart:rendered', this.onCartChanged);
      document.addEventListener('cart:updated', this.onCartChanged);

      this.addEventListener('click', this.onModalClick);
      this.addEventListener('submit', this.onModalSubmit);

      this.initializePrefetchObserver();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('pointerover', this.onPrefetch);
      document.removeEventListener('focusin', this.onPrefetch);
      document.removeEventListener('product:added', this.onCartChanged);
      document.removeEventListener('cart:rendered', this.onCartChanged);
      document.removeEventListener('cart:updated', this.onCartChanged);

      this.removeEventListener('click', this.onModalClick);
      this.removeEventListener('submit', this.onModalSubmit);

      this.prefetchObserver?.disconnect();
      this.refreshController?.abort();
    }

    initializePrefetchObserver() {
      const triggers = document.querySelectorAll(
        '[data-quick-view-open][data-product-url]',
      );

      if (!triggers.length) return;

      if (!('IntersectionObserver' in window)) {
        triggers.forEach((trigger) => this.prefetchTrigger(trigger));
        return;
      }

      this.prefetchObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.prefetchTrigger(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '300px 0px' },
      );

      triggers.forEach((trigger) => this.prefetchObserver.observe(trigger));
    }

    onPrefetch(event) {
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(
        '[data-quick-view-open][data-product-url]',
      );

      if (trigger) this.prefetchTrigger(trigger);
    }

    prefetchTrigger(trigger) {
      const productUrl = trigger.dataset.productUrl;
      if (!productUrl) return;

      this.fetchProductMarkup(productUrl).catch(() => {});
    }

    async onDocumentClick(event) {
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest('[data-quick-view-open]');
      if (!trigger) return;

      const productUrl = trigger.dataset.productUrl;
      if (!productUrl) return;

      event.preventDefault();
      await this.open(trigger, productUrl);
    }

    onDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') this.trapFocus(event);
    }

    onModalClick(event) {
      if (!(event.target instanceof Element)) return;

      const closeButton = event.target.closest('[data-quick-view-close]');
      if (closeButton) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target === this) this.close();
    }

    async onModalSubmit(event) {
      if (!(event.target instanceof HTMLFormElement)) return;

      const form = event.target.closest('form[data-type="add-to-cart-form"]');
      if (!form || !this.contains(form)) return;

      const submitButton =
        event.submitter instanceof HTMLElement
          ? event.submitter
          : form.querySelector('[data-add-to-cart]');

      /*
       * Chỉ chặn nút Add to cart.
       * Accelerated checkout / Buy it now vẫn để Shopify xử lý.
       */
      if (
        submitButton &&
        !submitButton.matches('[data-add-to-cart], [name="add"]')
      ) {
        return;
      }

      /*
       * Chặn ngay từ đầu để form không bao giờ rơi về submit HTML mặc định.
       * stopImmediatePropagation cũng ngăn listener Add to cart toàn cục
       * xử lý lại cùng một form Quick View.
       */
      event.preventDefault();
      event.stopImmediatePropagation();

      const cartBehavior = form.dataset.cartBehavior || 'page';
      const cartDrawer = document.querySelector('cart-drawer');
      const errorElement =
        form.querySelector('[data-add-to-cart-error]') ||
        form.querySelector('[data-product-form-status]');
      const fallbackError =
        form.dataset.addError ||
        'Unable to add this product to your cart. Please try again.';

      this.setButtonLoading(submitButton, true);
      this.clearFormError(errorElement);

      try {
        const formData = new FormData(form);

        /*
         * Dùng URL locale-aware chính thức của Shopify.
         */
        const cartAddUrl = new URL(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          window.location.origin,
        );

        if (cartDrawer) {
          formData.set(
            'sections',
            form.dataset.cartSectionId || 'cart-drawer',
          );
          formData.set('sections_url', window.location.pathname);
        } else {
          formData.delete('sections');
          formData.delete('sections_url');
        }

        const response = await fetch(cartAddUrl.toString(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: formData,
        });

        const result = await this.parseCartResponse(
          response,
          fallbackError,
        );

        /*
         * Đóng Quick View trước, sau đó mới cập nhật cart drawer.
         * Tránh hai popup cùng tồn tại và tranh chấp overflow/backdrop.
         * Khi cart_type là "page", cart-drawer tự bỏ qua việc mở
         * (xem guard trong CartDrawer.open()) nên chỉ số lượng được cập nhật.
         */
        this.close(false);

        if (cartDrawer) {
          const sectionId =
            cartDrawer.dataset.sectionId || 'cart-drawer';
          const shouldOpen = cartBehavior === 'drawer';

          if (
            result?.sections?.[sectionId] &&
            typeof window.AmerceCartDrawer?.render === 'function'
          ) {
            window.AmerceCartDrawer.render(result, shouldOpen);
          } else if (typeof cartDrawer.refresh === 'function') {
            await cartDrawer.refresh(shouldOpen);
          } else if (shouldOpen && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }

        document.dispatchEvent(
          new CustomEvent('product:added', {
            detail: {
              response: result,
              form,
              source: 'quick-view',
              open: cartBehavior === 'drawer',
            },
          }),
        );
      } catch (error) {
        console.error(error);
        this.showFormError(
          errorElement,
          error?.message || fallbackError,
        );
      } finally {
        this.setButtonLoading(submitButton, false);
      }
    }

    async onCartChanged(event) {
      this.cacheVersion += 1;
      this.productCache.clear();
      this.pendingRequests.clear();

      const addedForm = event?.detail?.form;
      const addedFromQuickView =
        event?.type === 'product:added' &&
        this.isOpen &&
        (event?.detail?.source === 'quick-view' ||
          (addedForm instanceof HTMLFormElement && this.contains(addedForm)));

      /*
       * Fallback an toàn: kể cả Add to cart được xử lý bởi code khác,
       * Quick View vẫn đóng nếu form phát sinh nằm trong modal này.
       */
      if (addedFromQuickView) {
        this.close(false);
        return;
      }

      if (!this.isOpen || !this.currentProductUrl) return;

      const selectedVariantId = this.querySelector(
        '[data-variant-id-input]',
      )?.value;

      const refreshUrl = new URL(
        this.currentProductUrl,
        window.location.origin,
      );

      if (selectedVariantId) {
        refreshUrl.searchParams.set('variant', selectedVariantId);
      }

      await this.refreshCurrentProduct(refreshUrl.toString());
    }

    getCacheKey(productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.hash = '';
      url.searchParams.delete('section_id');
      return `${url.pathname}${url.search}`;
    }

    getSectionUrl(productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', 'quick-view-product');
      return url;
    }

    async fetchProductMarkup(productUrl, options = {}) {
      const { force = false, signal } = options;
      const cacheKey = this.getCacheKey(productUrl);
      const requestVersion = this.cacheVersion;

      if (!force && this.productCache.has(cacheKey)) {
        return this.productCache.get(cacheKey);
      }

      if (!force && this.pendingRequests.has(cacheKey)) {
        return this.pendingRequests.get(cacheKey);
      }

      const request = fetch(this.getSectionUrl(productUrl), {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Quick View request failed: ${response.status}`);
          }

          return response.text();
        })
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const productContent = parsed.querySelector('[data-quick-view-product]');

          if (!productContent) {
            throw new Error('Quick View content was not found.');
          }

          const markup = productContent.outerHTML;

          if (requestVersion === this.cacheVersion) {
            this.productCache.set(cacheKey, markup);
          }

          return markup;
        })
        .finally(() => {
          this.pendingRequests.delete(cacheKey);
        });

      this.pendingRequests.set(cacheKey, request);
      return request;
    }

    async open(trigger, productUrl) {
      if (!this.content) return;

      this.activeTrigger = trigger;
      this.currentProductUrl = productUrl;
      this.clearError();

      trigger.toggleAttribute('disabled', true);
      trigger.setAttribute('aria-busy', 'true');

      try {
        const markup = await this.fetchProductMarkup(productUrl);
        this.renderProduct(markup);

        trigger.setAttribute('aria-expanded', 'true');
        this.hidden = false;
        this.setAttribute('aria-hidden', 'false');

        document.documentElement.classList.add('overflow-hidden');
        document.body.classList.add('overflow-hidden');

        requestAnimationFrame(() => {
          this.classList.add('show');
          this.isOpen = true;

          this.querySelector('[data-quick-view-close]')?.focus({
            preventScroll: true,
          });
        });
      } catch (error) {
        console.error(error);
        this.showError(
          this.dataset.errorMessage || 'Unable to load this product.',
        );
      } finally {
        trigger.toggleAttribute('disabled', false);
        trigger.removeAttribute('aria-busy');
      }
    }

    async refreshCurrentProduct(productUrl) {
      this.refreshController?.abort();
      this.refreshController = new AbortController();

      try {
        const markup = await this.fetchProductMarkup(productUrl, {
          force: true,
          signal: this.refreshController.signal,
        });

        if (!this.isOpen) return;
        this.renderProduct(markup);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    }

    renderProduct(markup) {
      const template = document.createElement('template');
      template.innerHTML = markup.trim();

      const productContent = template.content.firstElementChild;
      if (!productContent) throw new Error('Quick View content is invalid.');

      this.content.replaceChildren(productContent);
      this.initializePaymentButtons();
    }

    close(restoreFocus = true) {
      this.classList.remove('show');
      this.setAttribute('aria-hidden', 'true');
      this.isOpen = false;

      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');

      if (this.activeTrigger) {
        this.activeTrigger.setAttribute('aria-expanded', 'false');
      }

      window.setTimeout(() => {
        if (this.isOpen) return;
        this.hidden = true;
        this.content?.replaceChildren();
        this.clearError();
      }, 300);

      if (restoreFocus && this.activeTrigger instanceof HTMLElement) {
        this.activeTrigger.focus({ preventScroll: true });
      }
    }

    initializePaymentButtons() {
      if (typeof window.Shopify?.PaymentButton?.init === 'function') {
        window.Shopify.PaymentButton.init();
      }
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
          ].join(','),
        ),
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setButtonLoading(button, loading) {
      if (!(button instanceof HTMLElement)) return;
      button.toggleAttribute('disabled', loading);
      button.setAttribute('aria-busy', String(loading));
      button.classList.toggle('is-loading', loading);
    }

    clearFormError(errorElement) {
      if (!(errorElement instanceof HTMLElement)) return;
      errorElement.textContent = '';
      errorElement.hidden = true;
    }

    showFormError(errorElement, message) {
      if (!(errorElement instanceof HTMLElement)) return;
      errorElement.textContent = message;
      errorElement.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    async parseCartResponse(response, fallbackError) {
      const contentType = response.headers.get('content-type') || '';
      const responseText = await response.text();
      let result = null;

      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch (error) {
        result = null;
      }

      if (!result || typeof result !== 'object') {
        console.error('Unexpected Shopify cart response.', {
          status: response.status,
          url: response.url,
          redirected: response.redirected,
          contentType,
          preview: responseText.slice(0, 300),
        });

        throw new Error(fallbackError);
      }

      if (!response.ok) {
        throw new Error(
          result.description ||
            result.message ||
            fallbackError,
        );
      }

      return result;
    }
  }

  if (!customElements.get('quick-view-modal')) {
    customElements.define('quick-view-modal', QuickViewModal);
  }
})();
